import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV_LINKS, waLink, DEFAULT_WA_MESSAGE } from "@/config/siteConfig";
import { useSiteSettings, getPhones, getWaNumber, telHref } from "@/lib/siteSettings";
import { scrollToId } from "@/lib/scroll";
import { Logo } from "@/components/Logo";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { site } = useSiteSettings();
  const wa = waLink(DEFAULT_WA_MESSAGE, getWaNumber(site));
  const callHref = telHref(getPhones(site)[0].number);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-line transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(11,31,51,0.08)]" : ""
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <button onClick={() => go("#home")} className="shrink-0" aria-label="RK Real Estate home" data-testid="header-logo-button">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-auto mr-7" data-testid="header-nav">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              onClick={() => go(link.href)}
              data-testid={`header-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative text-[14px] font-medium text-ink hover:text-navy transition-colors duration-200 group py-2"
            >
              {link.label}
              <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="bg-gold text-navy hover:bg-gold-dark hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-5 py-2.5 text-[14px] font-semibold flex items-center gap-2 shadow-md shadow-gold/25"
          >
            <MessageCircle size={16} /> WhatsApp
          </a>
          <a
            href={callHref}
            data-testid="header-call-now-button"
            className="border border-gold text-navy hover:bg-gold/10 hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-5 py-2.5 text-[14px] font-semibold flex items-center gap-2"
          >
            <Phone size={16} /> Call Now
          </a>
        </div>

        <div className="lg:hidden ml-auto flex items-center gap-1">
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp Us"
            data-testid="header-mobile-whatsapp-button"
            className="p-2 text-navy hover:text-gold transition-colors"
          >
            <MessageCircle size={23} />
          </a>
          <button
            className="text-navy p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="header-mobile-menu-button"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-line"
            data-testid="header-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(link.href)}
                  data-testid={`header-mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left py-3.5 text-[15px] font-medium text-ink border-b border-line/70 last:border-0"
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
